import { CONFIG } from '../config.js';
import { safeFetchClient } from '../utils/safe-fetch-client.js';
import { getCurrentSession } from './auth.service.js';
import { listPagos as repoListPagos } from '../infrastructure/pagos.repository.js';

/**
 * Servicio de pagos (S5a, design.md AD8/AD13). Lecturas browser-directo bajo
 * RLS (`pagos.repository.js`); altas/anulaciones vía `POST
 * /api/payroll-movimientos` con el `access_token` de la sesión de staff
 * activa como Bearer — mismo patrón que `admin-adelantos.service.js` (S4):
 * el endpoint corre server-side con la service_role key porque las RPCs son
 * service_role-only.
 */

const ERROR_MESSAGES = {
    unauthorized: 'Tu sesión no es válida. Volvé a iniciar sesión.',
    forbidden: 'No tenés permiso para registrar pagos.',
    accion_invalida: 'Acción inválida.',
    liquidacion_id_requerido: 'No se pudo identificar la liquidación.',
    medio_invalido: 'Elegí un medio de pago válido (efectivo o transferencia).',
    monto_invalido: 'El monto debe ser un número mayor a cero.',
    fecha_invalida: 'La fecha no es válida.',
    liquidacion_inexistente: 'Esa liquidación no existe.',
    liquidacion_anulada: 'Esa liquidación fue anulada; no se pueden registrar pagos nuevos sobre ella.',
    // Spec payroll-payments "Register Payment Drawer" / tasks.md S5a.5 —
    // copia literal.
    monto_excede_neto: 'El pago supera el neto de la liquidación.',
    pago_id_requerido: 'No se pudo identificar el pago a anular.',
    motivo_requerido: 'Ingresá un motivo para anular el pago.',
    motivo_demasiado_largo: 'El motivo es demasiado largo.',
    pago_inexistente: 'Ese pago no existe.',
    pago_ya_anulado: 'Este pago ya fue anulado anteriormente.',
    no_disponible: 'El servicio no está disponible en este momento. No se registró nada; probá de nuevo en unos minutos.',
    error_interno: 'Ocurrió un error. Probá de nuevo.',
};

/**
 * @param {string|undefined} code
 * @returns {string}
 */
function mapMensaje(code) {
    return ERROR_MESSAGES[code] ?? ERROR_MESSAGES.error_interno;
}

/**
 * @typedef {{ ok: true, pagoId?: string } | {
 *   ok: false,
 *   kind: 'auth'|'validacion'|'incierto'|'error',
 *   mensaje: string,
 *   errorCode?: string,
 * }} PagoMovimientoResult
 */

/**
 * POST /api/payroll-movimientos con el Bearer token de la sesión de staff
 * activa. Único punto de fetch de este servicio para escrituras — mismo
 * `postMovimiento` shape que `admin-adelantos.service.js`, no reutilizado
 * literalmente entre módulos a propósito (cada uno mapea sus propios
 * códigos de error, mismo criterio que el resto de este panel).
 * @param {object} body
 * @returns {Promise<PagoMovimientoResult>}
 */
async function postMovimiento(body) {
    const session = await getCurrentSession();
    if (!session?.access_token) {
        return { ok: false, kind: 'auth', mensaje: ERROR_MESSAGES.unauthorized };
    }

    let res;
    try {
        res = await safeFetchClient(CONFIG.API.PAYROLL_MOVIMIENTOS, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify(body),
        });
    } catch {
        // Falla de transporte AMBIGUA por definición (mismo criterio que
        // crearAdelanto/anularLiquidacion): no hay forma de distinguir "la
        // RPC nunca corrió" de "corrió y la respuesta se perdió" — nunca
        // "error, probá de nuevo" a ciegas para una escritura.
        return {
            ok: false,
            kind: 'incierto',
            mensaje: 'Se cortó la conexión. No sabemos si se aplicó — revisá la lista antes de reintentar.',
        };
    }

    const responseBody = await res.json().catch(() => ({}));

    if (res.ok) {
        return { ok: true, pagoId: responseBody.pago_id };
    }
    if (res.status === 401 || res.status === 403) {
        return { ok: false, kind: 'auth', mensaje: mapMensaje(responseBody.error), errorCode: responseBody.error };
    }
    // 400/404/409 son los rechazos de negocio de registrar_pago/anular_pago
    // — SIEMPRE 'validacion' (mismo criterio que admin-adelantos.service.js).
    if (res.status === 400 || res.status === 404 || res.status === 409) {
        return { ok: false, kind: 'validacion', mensaje: mapMensaje(responseBody.error), errorCode: responseBody.error };
    }

    // The one 5xx that is NOT ambiguous: api/payroll-movimientos.js answers
    // 503 { error: 'no_disponible' } before any RPC runs (no Supabase
    // client), so nothing was written: a clean error, the caller keeps the
    // typed values for a retry. A 5xx without that exact body stays below.
    if (res.status >= 500 && responseBody.error === 'no_disponible') {
        return { ok: false, kind: 'error', mensaje: mapMensaje(responseBody.error), errorCode: responseBody.error };
    }

    // Any other 5xx is AMBIGUOUS for a write: a gateway 502/504 or a 500 after a
    // lost DB response can arrive once registrar_pago/anular_pago already committed.
    // Never a clean "probá de nuevo" — the caller must refresh the list
    // before letting the admin resubmit (same criterion as the transport
    // failure above).
    if (res.status >= 500) {
        return {
            ok: false,
            kind: 'incierto',
            mensaje: 'El servidor no respondió bien. No sabemos si se aplicó — revisá la lista antes de reintentar.',
            errorCode: responseBody.error,
        };
    }

    return { ok: false, kind: 'error', mensaje: mapMensaje(responseBody.error), errorCode: responseBody.error };
}

/**
 * Alta de un pago parcial sobre una liquidación (spec `payroll-payments`,
 * "Registering a Payment Never Exceeds Neto").
 * @param {{ liquidacionId: string, medio: string, monto: number, fecha: string, nota?: string|null }} params
 * @returns {Promise<PagoMovimientoResult>}
 */
export async function registrarPago({ liquidacionId, medio, monto, fecha, nota } = {}) {
    return postMovimiento({ action: 'pago_registrar', liquidacion_id: liquidacionId, medio, monto, fecha, nota: nota ?? null });
}

/**
 * Anulación auditada de un pago (spec `payroll-payments`, "Voiding a
 * Payment"). `motivo` se valida server-side (el endpoint revalida igual) —
 * este servicio no duplica esa validación de UX, mismo criterio que
 * `anularAdelanto`.
 * @param {{ pagoId: string, motivo: string }} params
 * @returns {Promise<PagoMovimientoResult>}
 */
export async function anularPago({ pagoId, motivo } = {}) {
    return postMovimiento({ action: 'pago_anular', pago_id: pagoId, motivo });
}

/**
 * Pagos (activos y anulados) de un conjunto de liquidaciones — lectura de
 * PANTALLA vía RLS (`pagos.repository.js`), thin pass-through sin try/catch
 * a propósito, mismo criterio que `loadAdelantos`: el caller (`js/admin.js`)
 * atrapa una falla real de Supabase y degrada la pantalla que corresponda.
 * @param {{ liquidacionIds?: string[] }} [params]
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error real
 */
export async function loadPagos(params = {}) {
    return repoListPagos(params);
}
